// SEQ-193: the "Month on month" widget body. Reuses the existing metric
// registry, safe-mode-aware formatter and the same table markup every other
// widget table uses (`.pnl-widget-table`), rather than a new visual language
// for one chart type.
//
// TWO ROW MODES, ONE COMPONENT, because that mirrors how the rest of the
// builder already models "group by": `groupBy: "company"` shows one row per
// chosen METRIC (the literal 24 September ask, "make our own patching one to
// compare months"); `groupBy: "project"` shows one row per hand-picked
// PROJECT for a single metric. See monthOnMonth.ts's file header for why
// project rows need their own bounded, on-demand fetch rather than the
// dashboard's shared request plan.
import { useMemo, type ReactNode } from "react";
import { AlertTriangle } from "lucide-react";
import { Money } from "../Money";
import type { PnlWidgetConfig } from "../../lib/pnl/pnlWidgetSchema";
import { monthOnMonthMetricKeys } from "../../lib/pnl/pnlWidgetSchema";
import { resolveMetricUnit } from "../../lib/pnl/pnlAnalytics";
import type { PnlMetricOption } from "../../features/pnlAnalytics/metricOption";
import type { PnlKeyView, PnlProjectNameSource } from "../../features/pnlAnalytics/widgetDataset";
import {
  buildMonthOnMonthTable,
  monthRowByPeriod,
  safeMonthsInRange,
  projectIdsFromFilters,
  MONTH_ON_MONTH_MAX_PROJECTS,
  capMonthOnMonthMetrics,
  type MonthOnMonthRangeContext,
  type MonthOnMonthSeriesInput,
} from "../../features/pnlAnalytics/monthOnMonth";
import { resolveWidgetFilters, type PnlDashboardPageFilters } from "../../features/pnlAnalytics/dashboardRequestKeys";
import type { PnlAnalyticsQueryFilters } from "../../features/pnlAnalytics/usePnlAnalytics";
import { useMonthOnMonthProjectRows } from "../../features/pnlAnalytics/useMonthOnMonthProjectRows";
import { deltaSentiment, type DeltaSentiment } from "../../features/pnlAnalytics/widgetPresentation";
import { ownEntry } from "../../features/pnlAnalytics/monthOnMonthProjectState";
import { buildMonthOnMonthRenderModel, LOAD_FAILED_TEXT } from "../../features/pnlAnalytics/monthOnMonthRenderModel";
import { WIDGET_EMPTY_MESSAGE } from "./PnlWidgetGenericCharts";
import { HowWorkedOut } from "../howWorkedOut/HowWorkedOut";
import type { MetricDefinition } from "../howWorkedOut/metricDefinition";
import { describeMetric, type DescribeContext } from "../../features/pnlAnalytics/pnlMetricDescriptions";

export interface PnlMonthOnMonthTableProps {
  config: PnlWidgetConfig;
  view: PnlKeyView;
  metrics: PnlMetricOption[];
  /**
   * Code review CRITICAL fix: the page's live filter bar. "Group by project"
   * mode issues its own per-project calls (useMonthOnMonthProjectRows), which
   * bypass the shared request plan entirely, so without this the widget's own
   * project picker and status filter reached the RPC un-narrowed by the page
   * filter -- the one rule every other widget's request key already honours
   * (resolveWidgetFilters in dashboardRequestKeys.ts: the page's filters,
   * narrowed by the widget's own, never widened).
   */
  queryFilters: PnlAnalyticsQueryFilters;
  /** Optional: only "group by project" mode needs project display names. Omitted call sites (e.g. the editor's live preview) fall back to raw ids. */
  projects?: PnlProjectNameSource[];
  /**
   * SEQ-240: the projects and dates this table's figures cover. When given,
   * each metric row label (company mode) gets a "How is this worked out"
   * trigger built by describeMetric with this context.
   */
  describeContext?: DescribeContext;
  /** SEQ-240: added to each row trigger's accessible name, e.g. "in Patching by month", so two tables' rows never share a name. */
  describeWhere?: string;
}

/** Today as 'YYYY-MM-DD' in the viewer's own calendar (local parts, never toISOString, which is UTC and would be a day out in Brisbane mornings). */
function localTodayIso(): string {
  const now = new Date();
  return `${String(now.getFullYear()).padStart(4, "0")}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

function MessageBody({ children }: { children: ReactNode }) {
  return (
    <div className="pnl-widget-empty">
      <span>{children}</span>
    </div>
  );
}

export function PnlMonthOnMonthTable({ config, view, metrics, queryFilters, projects, describeContext, describeWhere }: PnlMonthOnMonthTableProps) {
  const registryByKey = useMemo(() => new Map(metrics.map((m) => [m.metricKey, m])), [metrics]);
  const projectNameById = useMemo(() => new Map((projects ?? []).map((p) => [p.id, p.job_location])), [projects]);
  const isProjectMode = config.groupBy === "project";

  // Code review CRITICAL fix: the widget's own project picker and status
  // filter are narrowed by the page's live filters, the same rule every
  // other widget's request key applies (resolveWidgetFilters), never the
  // widget's filter alone. A project picked in the widget but excluded by
  // the page's own project filter is dropped here, silently, matching what
  // every other widget already does with a project outside its scope.
  const page: PnlDashboardPageFilters = useMemo(
    () => ({
      range: { from: queryFilters.from, to: queryFilters.to },
      projectIds: queryFilters.projectIds,
      statuses: queryFilters.statuses,
    }),
    [queryFilters.from, queryFilters.to, queryFilters.projectIds, queryFilters.statuses],
  );
  const resolvedFilters = useMemo(() => resolveWidgetFilters(config, page), [config, page]);
  // The widget's own picks, before the page filter narrows them, kept only to
  // tell "you have not picked any projects" apart from "the dashboard's own
  // project filter excludes every project you picked" (two different things
  // to fix). Everything that actually fetches or renders uses the resolved,
  // narrowed list below.
  const rawProjectIds = useMemo(() => (isProjectMode ? projectIdsFromFilters(config.filters) : []), [isProjectMode, config.filters]);
  const projectIds = isProjectMode ? resolvedFilters.projectIds : [];

  const projectData = useMonthOnMonthProjectRows({
    enabled: isProjectMode && projectIds.length > 0 && projectIds.length <= MONTH_ON_MONTH_MAX_PROJECTS,
    projectIds,
    metricKey: config.metric,
    from: view.from,
    to: view.to,
    statuses: resolvedFilters.statuses,
  });

  // A month-on-month table cannot enumerate "every month since the project
  // began": project_lifetime (and any other unbounded range) resolves to
  // {from: null, to: null}. Every other chart type is fine with that; this
  // one needs a real answer to "how many columns".
  if (view.from === null || view.to === null) {
    return (
      <MessageBody>
        Pick a bounded date range (not "Project lifetime") to compare months side by side.
      </MessageBody>
    );
  }

  // Security review finding (SEQ-193): a malformed or extremely wide custom
  // range used to reach `monthsInRange` unguarded and crash the whole page
  // for every viewer of a shared dashboard. `safeMonthsInRange` never
  // throws; a range it refuses shows a message instead of the table.
  const months = safeMonthsInRange(view.from, view.to);
  if (months === null) {
    return (
      <MessageBody>
        This date range cannot be compared month by month (it is not a valid range, or too wide). Pick a narrower
        range.
      </MessageBody>
    );
  }

  const rangeContext: MonthOnMonthRangeContext = { rangeToIso: view.to, todayIso: localTodayIso() };

  if (isProjectMode) {
    if (rawProjectIds.length === 0) {
      return <MessageBody>Add a project filter (1 to {MONTH_ON_MONTH_MAX_PROJECTS} projects) to compare them by month.</MessageBody>;
    }
    if (rawProjectIds.length > MONTH_ON_MONTH_MAX_PROJECTS) {
      return (
        <MessageBody>
          {rawProjectIds.length} projects are selected; a month-on-month table compares at most {MONTH_ON_MONTH_MAX_PROJECTS} at a
          time. Narrow the project filter to compare them by month.
        </MessageBody>
      );
    }
    if (projectIds.length === 0) {
      // Every project the widget picked exists, but none of them is inside
      // the dashboard's own project filter right now (resolveWidgetFilters
      // never widens past the page filter). Matches how every other widget
      // handles this: the page filter always wins, silently.
      return (
        <MessageBody>
          The dashboard's project filter excludes every project this widget picked. Widen the dashboard's project
          filter, or edit this widget's picks.
        </MessageBody>
      );
    }
    if (projectData.loading) {
      return <MessageBody>Loading…</MessageBody>;
    }

    const def = registryByKey.get(config.metric);
    if (!def) {
      return (
        <MessageBody>
          <AlertTriangle size={14} aria-hidden="true" style={{ verticalAlign: "-2px", marginRight: 4 }} />
          This widget uses <strong>{config.metric}</strong>, which no longer exists in the metric registry.
        </MessageBody>
      );
    }
    const unit = resolveMetricUnit(def);
    const sentiment = (deltaValue: number) => deltaSentiment(deltaValue > 0 ? "up" : deltaValue < 0 ? "down" : "flat", def);

    const series: MonthOnMonthSeriesInput[] = projectIds.map((id) => {
      const byMonth = monthRowByPeriod(ownEntry(projectData.rowsByProject, id) ?? []);
      return {
        key: id,
        label: projectNameById.get(id) ?? id,
        unit,
        valueForMonth: (month) => byMonth.get(month)?.metrics[config.metric] ?? null,
        loadFailed: ownEntry(projectData.errorByProject, id) !== undefined,
      };
    });
    const table = buildMonthOnMonthTable(months, series, config.showChange ?? false, rangeContext);
    return renderTable(table, "Project", config.showChange ?? false, sentiment);
  }

  // "company" mode: one row per chosen metric, values read straight off the
  // month-grain rows this widget's own request key already fetched (no extra
  // network call, unlike project mode).
  const metricKeys = capMonthOnMonthMetrics(monthOnMonthMetricKeys(config).filter((key) => registryByKey.has(key)));
  if (metricKeys.length === 0) {
    return (
      <MessageBody>
        <AlertTriangle size={14} aria-hidden="true" style={{ verticalAlign: "-2px", marginRight: 4 }} />
        None of this widget's chosen metrics exist in the metric registry any more. Edit the widget and pick a
        replacement.
      </MessageBody>
    );
  }

  const byMonth = monthRowByPeriod(view.rows);
  const series: MonthOnMonthSeriesInput[] = metricKeys.map((key) => {
    const def = registryByKey.get(key)!;
    return {
      key,
      label: def.displayName,
      unit: resolveMetricUnit(def),
      valueForMonth: (month) => byMonth.get(month)?.metrics[key] ?? null,
    };
  });
  const table = buildMonthOnMonthTable(months, series, config.showChange ?? false, rangeContext);
  const sentimentForRow = (rowKey: string, deltaValue: number) => {
    const def = registryByKey.get(rowKey);
    return deltaSentiment(deltaValue > 0 ? "up" : deltaValue < 0 ? "down" : "flat", def);
  };
  const describeRow = describeContext
    ? (rowKey: string) => {
        const def = registryByKey.get(rowKey);
        return def ? describeMetric(def, describeContext) : undefined;
      }
    : undefined;
  return renderTable(table, "Metric", config.showChange ?? false, undefined, sentimentForRow, describeRow, describeWhere);
}

function renderTable(
  table: ReturnType<typeof buildMonthOnMonthTable>,
  headingLabel: string,
  showChange: boolean,
  sentimentForValue?: (deltaValue: number) => DeltaSentiment,
  sentimentForRow?: (rowKey: string, deltaValue: number) => DeltaSentiment,
  describeRow?: (rowKey: string) => MetricDefinition | undefined,
  describeWhere?: string,
) {
  if (table.columns.length === 0 || table.rows.length === 0) {
    return <div className="pnl-widget-empty"><span>{WIDGET_EMPTY_MESSAGE}</span></div>;
  }

  const model = buildMonthOnMonthRenderModel(table, headingLabel, showChange, sentimentForValue, sentimentForRow);

  return (
    <div className="pnl-widget-table-scroll">
      <table className="pnl-widget-table">
        <thead>
          <tr>
            <th scope="col" data-align="left">
              {model.headingLabel}
            </th>
            {model.columnLabels.map((label, index) => (
              <th key={table.columns[index].periodMonth} scope="col" data-align="right">
                {label}
              </th>
            ))}
            {model.hasChangeColumn && (
              <th scope="col" data-align="right">
                {model.changeHeading}
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {model.rows.map((row) => {
            const rowDefinition = describeRow?.(row.key);
            return (
              <tr key={row.key}>
                {rowDefinition ? (
                  <td data-align="left">
                    <span className="hwo-heading">
                      {row.label}
                      <HowWorkedOut definition={rowDefinition} where={describeWhere} />
                    </span>
                  </td>
                ) : (
                  <td data-align="left">{row.label}</td>
                )}
                {row.loadFailed && (
                  <td colSpan={model.columnLabels.length + (model.hasChangeColumn ? 1 : 0)} data-align="left" style={{ color: "var(--text-secondary)" }}>
                    {LOAD_FAILED_TEXT}
                  </td>
                )}
                {row.cells.map((cell, index) => (
                  <Money as="td" key={table.columns[index].periodMonth} data-align="right">
                    {cell.text}
                  </Money>
                ))}
                {model.hasChangeColumn &&
                  !row.loadFailed &&
                  (row.change && row.change.colour ? (
                    <Money as="td" data-align="right" style={{ color: row.change.colour, fontWeight: 600 }}>
                      {row.change.text}
                    </Money>
                  ) : (
                    <Money as="td" data-align="right">{row.change?.text ?? "N/A"}</Money>
                  ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
